"use client";

import Sidebar from "@/components/student/Sidebar";
import CoursesListSkeleton from "@/components/student/CoursesListSkeleton";
import OnboardingTour from "@/components/student/OnboardingTour";
import { Suspense, useEffect, useState, useTransition } from "react";
import { usePathname, useRouter, Image, Link, useNavigate } from "@/src/lib/next-compat";
import * as api from "@/src/api/sdk.gen";
import { useStudentSidebar } from "@/src/context/StudentSidebarContext";

type OrgSyncState = {
    checked: boolean;
    userId: string | null;
    hasInaraXOrg: boolean;
    assignedOnProbe: boolean;
    checkedAt: number;
};

const ORG_SYNC_STATE_KEY = "inarax:org-sync-state";

export default function StudentLayoutClient({
    children,
    initialOrgSyncState,
}: {
    children: React.ReactNode;
    initialOrgSyncState: OrgSyncState | null;
}) {
    const { isDesktopCollapsed, isMobileOpen, setIsMobileOpen } = useStudentSidebar();
    const [isCoursesNavigationPending, startCoursesNavigation] = useTransition();
    const pathname = usePathname();
    const navigate = useNavigate();
    const showCoursesSkeleton = isCoursesNavigationPending && pathname !== "/courses";

    useEffect(() => {
        const handler = () => setIsMobileOpen(true);
        if (typeof window !== "undefined") {
            window.addEventListener("open-student-sidebar", handler);
        }
        return () => {
            if (typeof window !== "undefined") {
                window.removeEventListener("open-student-sidebar", handler);
            }
        };
    }, [setIsMobileOpen]);

    useEffect(() => {
        if (typeof window === "undefined") return;
        if (initialOrgSyncState?.checked === true) {
            sessionStorage.setItem(ORG_SYNC_STATE_KEY, JSON.stringify(initialOrgSyncState));
            return;
        }

        const cachedRaw = sessionStorage.getItem(ORG_SYNC_STATE_KEY);
        if (cachedRaw) {
            try {
                const parsed = JSON.parse(cachedRaw) as { checked?: boolean };
                if (parsed.checked === true) return;
            } catch {
                // Invalid cache payload, continue with fallback probe.
            }
        }

        let active = true;
        const probe = async () => {
            try {
                const { data, response, error } = await api.getCurrentUserProfileApiV1UsersMeGet();
                if (!active || error) return;
                const assigned = response?.headers.get("x-inarax-org-assigned") === "1";
                if (!error) {
                    const body = data as unknown as {
                        id?: string | number;
                        has_inarax_org?: boolean;
                    };
                    sessionStorage.setItem(
                        ORG_SYNC_STATE_KEY,
                        JSON.stringify({
                            checked: true,
                            userId: body.id ? String(body.id) : null,
                            hasInaraXOrg: body.has_inarax_org === true,
                            assignedOnProbe: assigned,
                            checkedAt: Date.now(),
                        }),
                    );
                }
                if (assigned) {
                }
            } catch {
                // Non-blocking probe; ignore failures.
            }
        };

        void probe();
        return () => {
            active = false;
        };
    }, [initialOrgSyncState]);

    return (
        <div className="min-h-screen bg-white flex">
            <Suspense fallback={null}>
                <OnboardingTour />
            </Suspense>
            <Sidebar
                isOpen={isMobileOpen}
                isCollapsed={isDesktopCollapsed}
                onClose={() => setIsMobileOpen(false)}
                onCoursesClick={() => {
                    startCoursesNavigation(() => {
                        navigate("/courses");
                    });
                }}
            />

            <div className="flex-1 flex flex-col min-w-0 w-full" suppressHydrationWarning data-tour="main-content">
                <main className="flex-grow">
                    {showCoursesSkeleton ? <CoursesListSkeleton /> : children}
                </main>
            </div>
        </div>
    );
}
